// src/components/admin/UserManagement.tsx - Redesigned to Match Admin Dashboard
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Paper,
  useTheme,
  alpha,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  SelectChangeEvent,
  Container,
  Divider,
  Tooltip,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  Close as CloseIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  AdminPanelSettings as AdminIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import * as userApi from '../../services/user';
import type { User } from '../../services/user';
import * as adminApi from '../../services/admin';

const MotionCard = motion(Card);
const MotionPaper = motion(Paper);

// Helper TypeScript interfaces
interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'info' | 'warning' | 'error';
}

interface StatCard {
  title: string;
  value: string;
  change: string;
  icon: React.ReactNode;
  color: 'primary' | 'secondary' | 'success' | 'warning' | 'info' | 'error';
  description: string;
}

const UserManagement: React.FC = () => {
  const theme = useTheme();
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userDialogOpen, setUserDialogOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterUserType, setFilterUserType] = useState<string>('all');
  const [inviteDialogOpen, setInviteDialogOpen] = useState<boolean>(false);
  const [inviteEmail, setInviteEmail] = useState<string>('');
  const [inviteUserType, setInviteUserType] = useState<'member' | 'expert'>('member');
  const [sendingInvite, setSendingInvite] = useState<boolean>(false);

  // Fetch users from API
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const data = await userApi.getAllUsers();
        setUsers(data);
      } catch (error) {
        console.error('Error fetching users:', error);
        setSnackbar({
          open: true,
          message: 'Failed to load users',
          severity: 'error'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Filter users based on search and filters (exclude admin users)
  const filteredUsers = users.filter(user => {
    // Exclude admin users from user management
    if (user.userType === 'admin') {
      return false;
    }

    const matchesSearch = searchTerm === '' ||
      user.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.organizationName && user.organizationName.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = filterStatus === 'all' || user.status === filterStatus;
    const matchesUserType = filterUserType === 'all' || user.userType === filterUserType;

    return matchesSearch && matchesStatus && matchesUserType;
  });

  const handleApproveUser = async (userId: string) => {
    try {
      await userApi.approveUser(userId);
      setUsers(prev => prev.map(user =>
        user.id === userId ? { ...user, status: 'approved' as const } : user
      ));
      setSnackbar({
        open: true,
        message: 'User approved successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error approving user:', error);
      setSnackbar({
        open: true,
        message: 'Failed to approve user',
        severity: 'error'
      });
    }
  };

  const handleRejectUser = async (userId: string) => {
    try {
      await userApi.rejectUser(userId);
      setUsers(prev => prev.map(user =>
        user.id === userId ? { ...user, status: 'rejected' as const } : user
      ));
      setSnackbar({
        open: true,
        message: 'User rejected',
        severity: 'info'
      });
    } catch (error) {
      console.error('Error rejecting user:', error);
      setSnackbar({
        open: true,
        message: 'Failed to reject user',
        severity: 'error'
      });
    }
  };

  const handleViewUser = (user: User) => {
    setSelectedUser(user);
    setUserDialogOpen(true);
  };

  const getStatusColor = (status: string): "warning" | "success" | "error" | "default" => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': return 'success';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const getUserTypeColor = (userType: string): "secondary" | "primary" => {
    return userType === 'expert' ? 'secondary' : 'primary';
  };

  const handleStatusFilterChange = (event: SelectChangeEvent) => {
    setFilterStatus(event.target.value);
  };

  const handleUserTypeFilterChange = (event: SelectChangeEvent) => {
    setFilterUserType(event.target.value);
  };

  const handleSendInvite = async () => {
    if (!inviteEmail) {
      setSnackbar({
        open: true,
        message: 'Please enter an email address',
        severity: 'error'
      });
      return;
    }

    try {
      setSendingInvite(true);
      await adminApi.inviteUser({
        email: inviteEmail,
        userType: inviteUserType
      });
      setSnackbar({
        open: true,
        message: 'Invitation sent successfully',
        severity: 'success'
      });
      setInviteDialogOpen(false);
      setInviteEmail('');
      setInviteUserType('member');
    } catch (error: any) {
      console.error('Error sending invite:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error?.message || 'Failed to send invitation',
        severity: 'error'
      });
    } finally {
      setSendingInvite(false);
    }
  };

  // Statistics data
  const stats: StatCard[] = [
    {
      title: 'Pending Approvals',
      value: users.filter(u => u.status === 'pending').length.toString(),
      change: '',
      icon: <ScheduleIcon />,
      color: 'warning',
      description: 'Awaiting review'
    },
    {
      title: 'Expert Applications',
      value: users.filter(u => u.userType === 'expert').length.toString(),
      change: '',
      icon: <SecurityIcon />,
      color: 'secondary',
      description: 'Expert registrations'
    },
    {
      title: 'Member Applications',
      value: users.filter(u => u.userType === 'member').length.toString(),
      change: '',
      icon: <PersonAddIcon />,
      color: 'primary',
      description: 'Member registrations'
    },
    {
      title: 'Total Applications',
      value: users.length.toString(),
      change: '',
      icon: <TrendingUpIcon />,
      color: 'info',
      description: 'All users'
    }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '70vh' }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary">Loading users...</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Hero Section */}
      <MotionPaper
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        elevation={0}
        sx={{
          mb: 4,
          p: { xs: 3, md: 4 },
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(
            theme.palette.secondary.main,
            0.8
          )})`,
          position: 'relative',
          overflow: 'hidden',
          color: 'white'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '40%',
            height: '100%',
            opacity: 0.1,
            background: `radial-gradient(circle, ${theme.palette.common.white} 0%, transparent 70%)`,
            transform: 'translateX(20%)'
          }}
        />

        <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <PeopleIcon sx={{ fontSize: 48 }} />
              User Management
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>
              Review and manage user registrations
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.8, maxWidth: 600 }}>
              Approve or reject pending applications and manage user accounts with comprehensive review tools
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            startIcon={<SendIcon />}
            onClick={() => setInviteDialogOpen(true)}
            sx={{
              bgcolor: 'white',
              color: theme.palette.primary.main,
              '&:hover': {
                bgcolor: alpha(theme.palette.common.white, 0.9)
              },
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
          >
            Send Invitation
          </Button>
        </Box>
      </MotionPaper>

      {/* Statistics Overview */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={stat.title}>
            <MotionCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -4, boxShadow: '0 12px 24px rgba(0,0,0,0.15)' }}
              sx={{
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette[stat.color].main, 0.05)}, ${alpha(
                  theme.palette[stat.color].light,
                  0.02
                )})`,
                border: `1px solid ${alpha(theme.palette[stat.color].main, 0.1)}`,
                height: '100%'
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette[stat.color].main, 0.1),
                      color: `${stat.color}.main`,
                      mr: 2
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Box>
                    <Typography variant="h4" fontWeight={700} color={`${stat.color}.main`}>
                      {stat.value}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stat.change}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {stat.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {stat.description}
                </Typography>
              </CardContent>
            </MotionCard>
          </Grid>
        ))}
      </Grid>

      {/* Filters and Search */}
      <MotionPaper 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        elevation={0}
        sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 3,
          background: alpha(theme.palette.background.paper, 0.7),
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      >
        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterIcon />
          Search & Filter
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                label="Status"
                onChange={handleStatusFilterChange}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>User Type</InputLabel>
              <Select
                value={filterUserType}
                label="User Type"
                onChange={handleUserTypeFilterChange}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="member">Members</MenuItem>
                <MenuItem value="expert">Experts</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {filteredUsers.length} results
            </Typography>
          </Grid>
        </Grid>
      </MotionPaper>

      {/* Users Table */}
      <MotionPaper
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        elevation={2}
        sx={{ borderRadius: 3, overflow: 'hidden' }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                <TableCell sx={{ fontWeight: 600, py: 2 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2 }}>Organization</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2 }}>Registration Date</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 2 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user, index) => (
                <TableRow 
                  key={user.id} 
                  hover
                  component={motion.tr}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  sx={{
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.02)
                    }
                  }}
                >
                  <TableCell sx={{ py: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: theme.palette.primary.main,
                          width: 48,
                          height: 48,
                          fontWeight: 600
                        }}
                      >
                        {user.firstName[0]}{user.lastName[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {user.firstName} {user.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {user.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.title}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 2.5 }}>
                    <Typography variant="body2" fontWeight={500}>
                      {user.organizationName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {user.segment} • {user.country}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 2.5 }}>
                    <Chip 
                      label={user.userType} 
                      color={getUserTypeColor(user.userType)}
                      size="small"
                      sx={{ 
                        textTransform: 'capitalize',
                        fontWeight: 600
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 2.5 }}>
                    <Chip 
                      label={user.status} 
                      color={getStatusColor(user.status)}
                      size="small"
                      sx={{ 
                        textTransform: 'capitalize',
                        fontWeight: 600
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 2.5 }}>
                    <Typography variant="body2">
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 2.5 }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          color="info"
                          size="small"
                          onClick={() => handleViewUser(user)}
                          sx={{ 
                            borderRadius: 2,
                            '&:hover': { 
                              bgcolor: alpha(theme.palette.info.main, 0.1) 
                            } 
                          }}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      {user.status === 'pending' && (
                        <>
                          <Tooltip title="Approve User">
                            <IconButton
                              color="success"
                              size="small"
                              onClick={() => handleApproveUser(user.id)}
                              sx={{ 
                                borderRadius: 2,
                                '&:hover': { 
                                  bgcolor: alpha(theme.palette.success.main, 0.1) 
                                } 
                              }}
                            >
                              <ApproveIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject User">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => handleRejectUser(user.id)}
                              sx={{ 
                                borderRadius: 2,
                                '&:hover': { 
                                  bgcolor: alpha(theme.palette.error.main, 0.1) 
                                } 
                              }}
                            >
                              <RejectIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Empty State */}
        {filteredUsers.length === 0 && (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No users found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm || filterStatus !== 'all' || filterUserType !== 'all' 
                ? 'Try adjusting your search criteria or filters'
                : 'All user registrations have been processed'
              }
            </Typography>
          </Box>
        )}
      </MotionPaper>

      {/* User Detail Dialog */}
      <Dialog 
        open={userDialogOpen} 
        onClose={() => setUserDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600}>
              User Registration Details
            </Typography>
            <IconButton onClick={() => setUserDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        {selectedUser && (
          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom color="primary">
                    Personal Information
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Name</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {selectedUser.firstName} {selectedUser.lastName}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Email</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {selectedUser.email}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Title</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {selectedUser.title}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Type</Typography>
                    <Chip 
                      label={selectedUser.userType} 
                      color={getUserTypeColor(selectedUser.userType)}
                      size="small"
                      sx={{ textTransform: 'capitalize', fontWeight: 600 }}
                    />
                  </Box>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom color="secondary">
                    Organization
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Organization</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {selectedUser.organizationName}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Segment</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {selectedUser.segment}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">Country</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {selectedUser.country}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Registration Date</Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleDateString() : 'N/A'}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom color="success.main">
                    Organization Brief
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body1">
                    {selectedUser.organizationBrief}
                  </Typography>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom color="info.main">
                    Professional Experience
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="body1">
                    {selectedUser.experience}
                  </Typography>
                </Card>
              </Grid>
              
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom color="warning.main">
                    Selected Sectors
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedUser.selectedSectors && selectedUser.selectedSectors.length > 0 ? (
                      selectedUser.selectedSectors.map((sector, index) => (
                        <Chip
                          key={index}
                          label={sector}
                          size="small"
                          variant="outlined"
                          color="primary"
                          sx={{ borderRadius: 2 }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No sectors selected
                      </Typography>
                    )}
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </DialogContent>
        )}
        <DialogActions sx={{ p: 3 }}>
          <Button 
            onClick={() => setUserDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Close
          </Button>
          {selectedUser && selectedUser.status === 'pending' && (
            <>
              <Button 
                onClick={() => {
                  handleRejectUser(selectedUser.id);
                  setUserDialogOpen(false);
                }}
                color="error"
                variant="outlined"
                sx={{ borderRadius: 2 }}
              >
                Reject
              </Button>
              <Button 
                onClick={() => {
                  handleApproveUser(selectedUser.id);
                  setUserDialogOpen(false);
                }}
                color="success"
                variant="contained"
                sx={{ borderRadius: 2 }}
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3 }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SendIcon />
              Send User Invitation
            </Typography>
            <IconButton onClick={() => setInviteDialogOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Invite a user to join the platform. They will receive an email with registration instructions.
          </Typography>
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="user@example.com"
            sx={{ mb: 3 }}
            InputProps={{
              sx: { borderRadius: 2 }
            }}
          />
          <FormControl fullWidth>
            <InputLabel>User Type</InputLabel>
            <Select
              value={inviteUserType}
              label="User Type"
              onChange={(e) => setInviteUserType(e.target.value as 'member' | 'expert')}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="expert">Expert</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setInviteDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
            disabled={sendingInvite}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendInvite}
            variant="contained"
            color="primary"
            sx={{ borderRadius: 2 }}
            disabled={sendingInvite}
            startIcon={sendingInvite ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {sendingInvite ? 'Sending...' : 'Send Invitation'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({...snackbar, open: false})}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({...snackbar, open: false})}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UserManagement;